import type {
  HubbellFeedbackSkipReason,
  HubbellFeedbackSkippedDivision,
  HubbellFeedbackUnpairedReason,
} from '@erp/rpc';
import type { TranslateFn } from 'src/lib/pricelist-utils';

import type { DropProblem } from './classifyFiles';
import type { StartBlocker } from './runDisplay';

/**
 * The card's wording for the run's client-neutral reasons and the page's
 * own states, so the view stays a layout. Each maps one enum to one string.
 */

export function dropProblemText(
  problem: DropProblem,
  gt: TranslateFn,
  /** The supplier's Results Workbook as the team names it ("FINAL.xlsx"). */
  results: string,
): string {
  switch (problem) {
    case 'second results workbook':
      return gt('Second Results Workbook — left out', {
        context:
          'Hubbell Feedback card: a second FINAL.xlsx was dropped; only the first is used',
      });
    case 'not a feedback input':
      return gt('Not {results} or a Customer Sales Summary — left out', {
        context:
          'Feedback card: a dropped file is neither the Results Workbook (named in {results}) nor a sales file',
        results,
      });
    case 'too many sales files':
      return gt('More than nine sales files — left out', {
        context:
          'Hubbell Feedback card: a tenth Customer Sales Summary was dropped',
      });
    case 'names no division':
      return gt('Its name is not a Division — left out', {
        context:
          'Hubbell Feedback card: a Customer Sales Summary whose file name does not start with a Division name',
      });
    case 'division already has a file':
      return gt('Its Division already has a file — left out', {
        context:
          'Hubbell Feedback card: a second Customer Sales Summary for the same Division',
      });
    case 'unreadable':
      return gt('Could not be opened as .xlsx — left out', {
        context:
          'Hubbell Feedback card: the browser could not read a dropped file as an Excel workbook',
      });
  }
}

/** Why Start is disabled; null when the reason is plain from the card itself. */
export function startBlockerText(
  blocker: StartBlocker,
  gt: TranslateFn,
): string | null {
  switch (blocker) {
    case 'active run':
      return gt('A run is in progress; Start is available once it finishes.', {
        context: 'Hubbell Feedback card: why the Start button is disabled',
      });
    case 'not allowed':
      return gt('Starting a run needs the Pricing create permission.', {
        context: 'Hubbell Feedback card: why the Start button is disabled',
      });
    case 'reading':
      return gt('Reading the dropped files…', {
        context:
          'Hubbell Feedback card: files are still being classified before Start',
      });
    case 'starting':
    case 'incomplete drop':
      return null;
  }
}

export function skipReasonText(
  reason: HubbellFeedbackSkipReason,
  gt: TranslateFn,
  /** The supplier's Results Workbook as the team names it ("FINAL.xlsx"). */
  results: string,
): string {
  switch (reason) {
    case 'no sales file':
      return gt('Not selected', {
        context:
          'Hubbell Feedback card: why a Division tab was skipped (no sales file dropped and not marked "no sales file")',
      });
    case 'two sales files name this Division':
      return gt('Two sales files name this Division', {
        context: 'Hubbell Feedback card: why a Division tab was skipped',
      });
    case 'pairing mismatch':
      return gt('Sales file does not match this tab', {
        context:
          'Hubbell Feedback card: why a Division tab was skipped (overlap under 75%)',
      });
    case 'tab not in the Division table':
      return gt('Tab not in the Division table', {
        context: 'Hubbell Feedback card: why a Division tab was skipped',
      });
    case 'tab not in the Results Workbook':
      return gt('Tab not in {results}', {
        context:
          'Feedback card: why a Division tab was skipped; {results} names the Results Workbook',
        results,
      });
  }
}

/** The skip reason, with the file and overlap share when the skip is about a pairing. */
export function skippedDetailText(
  outcome: HubbellFeedbackSkippedDivision,
  gt: TranslateFn,
  results: string,
): string {
  const parts = [skipReasonText(outcome.reason, gt, results)];
  if (outcome.salesFileName) parts.push(outcome.salesFileName);
  if (outcome.share !== null) {
    parts.push(
      gt('match {percent}%', {
        context:
          'Hubbell Feedback card: the overlap share of a mismatched pairing',
        percent: String(Math.round(outcome.share * 100)),
      }),
    );
  }
  return parts.join(' · ');
}

export function unpairedReasonText(
  reason: HubbellFeedbackUnpairedReason,
  gt: TranslateFn,
  /** The supplier's Results Workbook as the team names it ("FINAL.xlsx"). */
  results: string,
): string {
  switch (reason) {
    case 'names no Division':
      return gt('Its name is not a Division', {
        context:
          'Hubbell Feedback card: why a sales file paired with no Division tab',
      });
    case 'names a Division another file also names':
      return gt('Another file names the same Division', {
        context:
          'Hubbell Feedback card: why a sales file paired with no Division tab',
      });
    case 'names a Division that has no sales file':
      return gt('Names a Division that has no sales file', {
        context:
          'Hubbell Feedback card: why a sales file paired with no Division tab',
      });
    case 'names a Division whose tab is not in the Results Workbook':
      return gt('Its Division tab is not in {results}', {
        context:
          'Feedback card: why a sales file paired with no Division tab; {results} names the Results Workbook',
        results,
      });
  }
}
